import type { HTMLAttributeAnchorTarget, ReactNode } from 'react';
import Link from 'next/link';
import { clsx } from 'clsx';
import { ArrowOutIcon, ArrowRightIcon } from '../icons';
import styles from './marketing.module.css';

interface TextLinkProps {
  href: string;
  target?: HTMLAttributeAnchorTarget;
  rel?: string;
  children: ReactNode;
  /**
   * `forward` for a link that continues the story on this site, `out` for one
   * that leaves it, `none` for a plain underlined link. Decorative only; the
   * link text carries the meaning.
   */
  arrow?: 'forward' | 'out' | 'none';
  variant?: 'prose' | 'title';
  className?: string;
  'aria-label'?: string;
}

/**
 * The inline text link: inherited ink, 1px underline at a 4px offset that fades
 * on hover, an optional arrow that shifts 3px on hover and focus. Internal
 * routes render through the Next link so they prefetch.
 */
export function TextLink({
  href,
  target,
  rel,
  children,
  arrow = 'forward',
  variant = 'prose',
  className,
  'aria-label': ariaLabel,
}: TextLinkProps): React.ReactElement {
  const classes = clsx(
    styles.link,
    arrow !== 'none' && styles.linkArrowed,
    variant === 'title' && styles.titleLink,
    className
  );
  const glyph =
    arrow === 'forward' ? (
      <ArrowRightIcon variant="hairline" className={styles.arrowForward} />
    ) : arrow === 'out' ? (
      <ArrowOutIcon className={styles.arrowOut} />
    ) : null;
  // String titles keep the last word attached to the arrow without fixing line breaks.
  const ending =
    variant === 'title' && typeof children === 'string' && glyph
      ? children.match(/^([\s\S]*\s)?(\S+)$/)
      : null;
  const content = ending ? (
    <>
      {ending[1]}
      <span className={styles.linkEnding}>
        {ending[2]}
        {glyph}
      </span>
    </>
  ) : (
    <>
      {children}
      {glyph}
    </>
  );
  const external = /^https?:\/\//.test(href);

  if (external || href.startsWith('#')) {
    return (
      <a href={href} target={target} rel={rel} className={classes} aria-label={ariaLabel}>
        {content}
      </a>
    );
  }
  return (
    <Link href={href} target={target} rel={rel} className={classes} aria-label={ariaLabel}>
      {content}
    </Link>
  );
}
